import {
  cosineSimilarity,
  decodeImageEmbedding,
  meanEmbedding,
  softmaxFromCosines,
} from '@sb/dinofast';
import * as log from '@sb/log';

import type EquipmentInterface from '../EquipmentInterface';

import { callComputeDinoImageEmbedding } from './runLocate';
import type {
  ClassifyResult,
  RegionOfInterest,
  VisionInterface,
} from './VisionInterface';

const ns = log.namespace('classifyDinoFast');

// Temperature for softmax over cosine scores. Picked so that a ~0.02 cosine
// gap between top-1 and top-2 lands around ~60/40 probability — close enough
// to 50/50 that a 70% confidence slider rejects it, but not so flat that any
// non-trivial gap collapses. Increase to soften, decrease to sharpen.
const DINOFAST_SOFTMAX_TEMPERATURE = 0.07;

export interface DinoFastClassifyExampleInput {
  storageID: string;
  embedding: {
    // L2-normalized CLS image embedding (server-side); stored as fp32 floats
    // client-side so the wire decode happens once at capture time.
    values: number[];
    // Server-emitted model_version with an "-img" suffix so these can't
    // accidentally cross with the patch-tensor embedding space.
    modelVersion: string;
  };
}

export interface DinoFastClassifyClassInput {
  name: string;
  examples: DinoFastClassifyExampleInput[];
}

export interface ClassifyDinoFastInput {
  vision: VisionInterface;
  equipment: EquipmentInterface;
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  classes: DinoFastClassifyClassInput[];
  embeddingModelVersion: string;
  precision: string;
  gpuTaskRunnerId: string;
  // Optional cache of (storageID|modelVersion|precision|gpuTaskRunnerId) →
  // re-encoded embedding values, so repeated calls (e.g. preview polling, or
  // a Step instance invoked in a loop) don't re-fetch and re-encode each
  // iteration. Caller owns lifetime.
  reencodeCache?: Map<string, number[]>;
}

export async function classifyDinoFast(
  input: ClassifyDinoFastInput,
): Promise<ClassifyResult[]> {
  const {
    vision,
    equipment,
    image,
    regionOfInterest,
    classes,
    embeddingModelVersion,
    precision,
    gpuTaskRunnerId,
    reencodeCache,
  } = input;

  // Crop the query frame to the user's ROI before encoding so embeddings
  // reflect only the configured region — example images are already cropped
  // at capture time, so this aligns query/example coverage.
  const { croppedImage } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  const classPrototypes: Array<{
    name: string;
    freshEmbeddings: Array<{ values: number[] }>;
  }> = [];

  for (const cls of classes) {
    const freshEmbeddings: Array<{ values: number[] }> = [];

    for (const example of cls.examples) {
      if (example.embedding.modelVersion === embeddingModelVersion) {
        freshEmbeddings.push({ values: example.embedding.values });

        continue;
      }

      const cacheKey = `${example.storageID}|${embeddingModelVersion}|${precision}|${gpuTaskRunnerId}`;
      const cached = reencodeCache?.get(cacheKey);

      if (cached) {
        freshEmbeddings.push({ values: cached });

        continue;
      }

      try {
        const bytes = await vision.fetchStoredImage(example.storageID);

        const response = await callComputeDinoImageEmbedding({
          image: bytes,
          modelVersion: embeddingModelVersion,
          precision,
          equipment,
          vision,
          gpuTaskRunnerId,
        });

        if (response.error || !response.data) {
          log.warn(ns`re-encode`, 'Re-encode failed for stored image', {
            storageID: example.storageID,
            error: response.error,
          });

          continue;
        }

        const { data } = response;
        const decoded = decodeImageEmbedding(data.embeddingDim, data.values);
        const values = Array.from(decoded);

        reencodeCache?.set(cacheKey, values);
        freshEmbeddings.push({ values });
      } catch (err) {
        log.warn(ns`re-encode`, 'Exception re-encoding stored image', {
          storageID: example.storageID,
          error: err,
        });
      }
    }

    if (freshEmbeddings.length === 0) {
      log.warn(ns`class-skip`, 'Skipping class with no usable embeddings', {
        className: cls.name,
      });

      continue;
    }

    classPrototypes.push({ name: cls.name, freshEmbeddings });
  }

  if (classPrototypes.length === 0) {
    // Treat this like the query-embedding error path below: if every class
    // dropped out (re-encode failures or all classes had zero examples),
    // there's nothing meaningful to classify against. Silently returning []
    // would let the caller pick `fallbackValue`, masking infrastructure or
    // config errors and producing incorrect downstream actions.
    throw new Error(
      'DinoFast classify: no usable class embeddings (all classes were skipped)',
    );
  }

  const queryResponse = await callComputeDinoImageEmbedding({
    image: croppedImage,
    modelVersion: embeddingModelVersion,
    precision,
    equipment,
    vision,
    gpuTaskRunnerId,
  });

  if (queryResponse.error || !queryResponse.data) {
    throw new Error(
      `DinoFast query embedding failed: ${
        queryResponse.error?.message ?? 'no data returned'
      }`,
    );
  }

  const { data: queryData } = queryResponse;

  const queryEmbedding = decodeImageEmbedding(
    queryData.embeddingDim,
    queryData.values,
  );

  const cosines = classPrototypes.map(({ freshEmbeddings }) =>
    cosineSimilarity(queryEmbedding, meanEmbedding(freshEmbeddings)),
  );

  // Softmax requires ≥2 classes to be meaningful (single-class softmax is
  // always 1.0); fall back to raw cosine so the confidence slider still
  // functions as a similarity floor.
  const scores =
    cosines.length >= 2
      ? softmaxFromCosines(cosines, DINOFAST_SOFTMAX_TEMPERATURE)
      : cosines;

  const results: ClassifyResult[] = classPrototypes.map(({ name }, i) => ({
    name,
    score: scores[i],
  }));

  results.sort((a, b) => b.score - a.score);

  return results;
}
